import { Injectable } from '@angular/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Platform } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable } from 'rxjs';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

@Injectable({
  providedIn: 'root'
})
export class SafeAreaService {
  private safeAreaInsets$ = new BehaviorSubject<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });

  private isInitialized = false;

  constructor(private platform: Platform) {
    this.initializeSafeArea();
    
    // Suscribirse a cambios en safe area para actualizar CSS
    this.safeAreaInsets$.subscribe(insets => {
      this.updateCSSCustomProperties(insets);
    });
  }

  /**
   * Actualiza las CSS custom properties en el DOM
   */
  private updateCSSCustomProperties(insets: SafeAreaInsets): void {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
      root.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
      root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
      root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
      
      console.log('Safe Area CSS properties updated:', insets);
    }
  }

  /**
   * Inicializa el safe area
   */
  private async initializeSafeArea(): Promise<void> {
    try {
      if (!this.platform.is('hybrid')) {
        // En web, usar valores por defecto o CSS env() si está disponible
        this.initializeWebSafeArea();
        return;
      }

      // Obtener las insets del safe area
      const result = await SafeArea.getSafeAreaInsets();
      
      if (result && result.insets) {
        this.safeAreaInsets$.next({
          top: result.insets.top || 0,
          bottom: result.insets.bottom || 0,
          left: result.insets.left || 0,
          right: result.insets.right || 0
        });
      }

      this.isInitialized = true;
      console.log('Safe Area initialized:', result);

    } catch (error) {
      console.error('Error initializing Safe Area:', error);
      
      // Fallback a valores por defecto
      this.safeAreaInsets$.next({
        top: this.platform.is('ios') ? 44 : 24, // Status bar height
        bottom: this.platform.is('ios') ? 34 : 0, // Home indicator
        left: 0,
        right: 0
      });
    }
  }

  /**
   * Inicializa safe area para web usando CSS env()
   */
  private initializeWebSafeArea(): void {
    if (typeof window !== 'undefined' && window.CSS && window.CSS.supports) {
      const topSupported = window.CSS.supports('padding-top: env(safe-area-inset-top)');
      
      if (topSupported) {
        // El navegador soporta CSS env(), usar valores calculados
        setTimeout(() => {
          const computedStyle = getComputedStyle(document.documentElement);
          
          this.safeAreaInsets$.next({
            top: this.parseCSSValue(computedStyle.getPropertyValue('env(safe-area-inset-top)')) || 0,
            bottom: this.parseCSSValue(computedStyle.getPropertyValue('env(safe-area-inset-bottom)')) || 0,
            left: this.parseCSSValue(computedStyle.getPropertyValue('env(safe-area-inset-left)')) || 0,
            right: this.parseCSSValue(computedStyle.getPropertyValue('env(safe-area-inset-right)')) || 0
          });
        }, 100);
      }
    }
  }

  /**
   * Convierte valores CSS a números
   */
  private parseCSSValue(value: string): number {
    if (!value) return 0;
    const parsed = parseFloat(value.replace('px', ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Obtiene las insets del safe area como Observable
   */
  getSafeAreaInsets(): Observable<SafeAreaInsets> {
    return this.safeAreaInsets$.asObservable();
  }

  /**
   * Obtiene las insets actuales del safe area
   */
  getCurrentSafeAreaInsets(): SafeAreaInsets {
    return this.safeAreaInsets$.value;
  }

  /**
   * Verifica si el safe area está inicializado
   */
  isInitializedSafeArea(): boolean {
    return this.isInitialized;
  }

  /**
   * Obtiene el padding top para el header
   */
  getHeaderTopPadding(): number {
    return this.safeAreaInsets$.value.top;
  }

  /**
   * Obtiene el padding bottom para elementos en la parte inferior
   */
  getBottomPadding(): number {
    return this.safeAreaInsets$.value.bottom;
  }

  /**
   * Obtiene las insets como CSS custom properties
   */
  getSafeAreaCSSProperties(): { [key: string]: string } {
    const insets = this.safeAreaInsets$.value;
    return {
      '--safe-area-inset-top': `${insets.top}px`,
      '--safe-area-inset-bottom': `${insets.bottom}px`,
      '--safe-area-inset-left': `${insets.left}px`,
      '--safe-area-inset-right': `${insets.right}px`
    };
  }

  /**
   * Actualiza manualmente las insets (útil para testing)
   */
  updateSafeAreaInsets(insets: Partial<SafeAreaInsets>): void {
    const currentInsets = this.safeAreaInsets$.value;
    this.safeAreaInsets$.next({
      ...currentInsets,
      ...insets
    });
  }

  /**
   * Recalcula las insets (útil cuando cambia la orientación)
   */
  async recalculateSafeArea(): Promise<void> {
    if (this.platform.is('hybrid')) {
      try {
        const result = await SafeArea.getSafeAreaInsets();
        
        if (result && result.insets) {
          this.safeAreaInsets$.next({
            top: result.insets.top || 0,
            bottom: result.insets.bottom || 0,
            left: result.insets.left || 0,
            right: result.insets.right || 0
          });
        }
      } catch (error) {
        console.error('Error recalculating Safe Area:', error);
      }
    }
  }
}